import {useMemo} from 'react'
import {useSelector} from 'react-redux'

import {resolveCapabilities} from '../../../brand/capabilities/resolveCapabilities'
import {getBrand} from '../modules/selectors'
import {useCapabilityRuntimeContext} from './CapabilityRuntimeContext'

import type {ResolvedCapabilities} from '../../../brand/capabilities/types/core'

/**
 * Primary API for React code to consume brand capabilities. Resolves
 * static values and resolver functions transparently — consumers always
 * receive plain values.
 *
 * The runtime context (`isMobile`, `locale`, …) is read from
 * `<CapabilityRuntimeContext.Provider>`, which must be mounted near the
 * top of the app shell. The provider's value comes from
 * `useCapabilityContext()` in `@vio/shared/src/whiteLabel/hooks/` — that
 * hook owns the atlas-dependent assembly and can't live in `core`
 * without creating a `core → atlas` circular package dependency.
 */
export const useBrandCapabilities = (): ResolvedCapabilities => {
  const brand = useSelector(getBrand)
  const ctx = useCapabilityRuntimeContext()

  return useMemo(
    () => resolveCapabilities(brand.capabilities, ctx),
    [brand.capabilities, ctx]
  )
}
